import FreeText from '@algbra/ui/components/FreeText/FreeText'
import Permalink from '@algbra/ui/components/Permalink/Permalink'
import { resolveAsAttachment, resolveAsImage } from '@algbra/ui/resolvers'
import classNames from 'classnames'
import { filter, find, map } from 'rambdax'
import { ReactNode, useEffect, useState } from 'react'
import axios from 'axios'
import { PrimaryNavLink, SecondaryNavLink } from '../Header/Header'
import styles from './HeaderMenu.module.scss'
import Icon from '@algbra/ui/components/Icon/Icon'
import { getIconByName } from '@algbra/ui/icons'
import { GatsbyImage } from 'gatsby-plugin-image'

export type HeaderMenuProps = {
  links: PrimaryNavLink[]
  subLinks: SecondaryNavLink[]
  isMobileMenuVisible?: boolean
  theme: string
  isCompact?: boolean
  onMenuToggle?: (isActive: boolean) => void
}
export function HeaderMenuPrimaryLink({
  title,
  children,
  className,
  isCompact,
  onMenuToggle,
  url,
  isClickable,
}: PrimaryNavLink & {
  children?: ReactNode
  className?: string
  isCompact?: boolean
  url?: string
  onMenuToggle?: (isOpen: boolean) => void
}) {
  const [activeMenu, setActiveMenu] = useState<boolean>(false)
  const setMenuView = (isActive: boolean) => {
    setActiveMenu(isActive)
    onMenuToggle && onMenuToggle(isActive)
  }

  return (
    <div
      className={classNames(
        styles.primaryNavLink,
        {
          [styles.active]: activeMenu,
          [styles.compact]: isCompact,
          [styles.pointer]: !children && url,
        },
        className
      )}
      onMouseEnter={() => setMenuView(true)}
      onMouseLeave={() => setMenuView(false)}
    >
      {isClickable ? (
        <Permalink
          className={styles.title}
          to={url}
          isExternal={false}
          isBlank={false}
        >
          {title}
        </Permalink>
      ) : (
        <span className={styles.title}>{title}</span>
      )}
      {children && (
        <div className={styles.children}>
          <div className={styles.childrenCentered}>
            <div className={styles.childrenBg}>{children}</div>
          </div>
        </div>
      )}
    </div>
  )
}
function FeaturesPanelCard({
  sub,
  isPlansColumn,
  descriptionOverride,
}: {
  sub: SecondaryNavLink
  isPlansColumn: boolean
  descriptionOverride?: string
}) {
  const { title, description, url, target, newBadge, icon, logoImage } = sub
  const panelDescription = descriptionOverride || description
  const image = resolveAsImage(logoImage)
  const iconUrl = resolveAsAttachment(icon)
  // Data-driven "All Plans" rule: a plans-column item that has an icon but
  // no logoImage (e.g. "All Plans") gets the inverse card treatment.
  const isInverse = isPlansColumn && !!iconUrl && !image

  return (
    <Permalink
      className={classNames(styles.featuresPanelCard, {
        [styles.featuresPanelCardInverse]: isInverse,
      })}
      activeClassName={styles.active}
      to={url}
      isExternal={!!target}
      isBlank={!!target}
    >
      {image ? (
        <GatsbyImage
          image={image}
          alt={title}
          className={styles.featuresPanelThumbnail}
          loading="lazy"
        />
      ) : (
        <div className={styles.featuresPanelIconSquare}>
          {iconUrl && (
            <img
              src={iconUrl}
              alt={title}
              className={styles.featuresPanelIcon}
            />
          )}
        </div>
      )}
      <div className={styles.featuresPanelTextBlock}>
        <div className={styles.featuresPanelTitleRow}>
          <span className={styles.featuresPanelTitle}>{title}</span>
          {newBadge && (
            <div className={styles.featuresPanelBadge}>
              <span>NEW</span>
            </div>
          )}
        </div>
        {panelDescription && (
          <span className={styles.featuresPanelDescription}>
            {panelDescription}
          </span>
        )}
      </div>
      <Icon
        icon={getIconByName('arrow-right')}
        fill="#1A1A1A"
        size={24}
        className={styles.featuresPanelArrow}
      />
    </Permalink>
  )
}
export function HeaderMenu({
  links,
  subLinks,
  onMenuToggle,
  isCompact,
  theme,
}: HeaderMenuProps) {
  // Same live rate source as HomeBoxEthicalSavings — feeds the Ethical
  // Savings card description in the features mega-menu. Falls back to the
  // CMS description while loading or if the fetch fails.
  const [topRate, setTopRate] = useState<{ rate?: number } | null>(null)
  useEffect(() => {
    axios
      .get('/.netlify/functions/topRate')
      .then(response => setTopRate(response.data))
      .catch(() => setTopRate(null))
  }, [])
  const topRateDescription =
    typeof topRate?.rate === 'number'
      ? `Earn up to ${topRate.rate.toFixed(2)}% EPR* on your money`
      : undefined

  return (
    <nav>
      <div
        className={classNames(styles.primaryNav, styles[`theme__${theme}`], {
          [styles.compact]: isCompact,
        })}
      >
        {map(link => {
          const filteredLinks = filter(
            sub => sub.parent === link.groupName && !sub.isBanner,
            subLinks
          )
          const banner = find(
            sub => sub.isBanner && link.groupName === sub.parent,
            subLinks
          )
          const isFeaturesMegaMenu = link.groupName === 'features'
          const plansColumnLinks = isFeaturesMegaMenu
            ? filter(sub => sub.parent === 'plans' && !sub.isBanner, subLinks)
            : []

          return (
            <HeaderMenuPrimaryLink
              key={link.id}
              {...link}
              isCompact={isCompact}
              onMenuToggle={onMenuToggle}
              className={classNames({
                [styles.twoCol]: link.twoCol,
              })}
            >
              {isFeaturesMegaMenu ? (
                (filteredLinks.length > 0 || plansColumnLinks.length > 0) && (
                  <div className={styles.featuresPanel}>
                    {filteredLinks.length > 0 && (
                      <div className={styles.featuresPanelColumn}>
                        <span className={styles.featuresPanelColumnHeader}>
                          Core Features
                        </span>
                        {map(
                          sub => (
                            <FeaturesPanelCard
                              key={sub.id}
                              sub={sub}
                              isPlansColumn={false}
                              descriptionOverride={
                                sub.url?.includes('ethical-savings')
                                  ? topRateDescription
                                  : undefined
                              }
                            />
                          ),
                          filteredLinks
                        )}
                      </div>
                    )}
                    {plansColumnLinks.length > 0 && (
                      <div className={styles.featuresPanelColumn}>
                        <span className={styles.featuresPanelColumnHeader}>
                          Plans
                        </span>
                        {map(
                          sub => (
                            <FeaturesPanelCard
                              key={sub.id}
                              sub={sub}
                              isPlansColumn={true}
                            />
                          ),
                          plansColumnLinks
                        )}
                      </div>
                    )}
                  </div>
                )
              ) : (
                filteredLinks.length > 0 && (
                  <>
                    <div className={styles.secondaryLinks}>
                      {map(
                        ({
                          title,
                          description,
                          logoImage,
                          id,
                          url,
                          target,
                          newBadge,
                        }: SecondaryNavLink) => {
                          return (
                            <Permalink
                              className={classNames(styles.secondaryLink, {
                                [styles.hasLogoImage]: !!logoImage,
                              })}
                              activeClassName={styles.active}
                              to={url}
                              key={id}
                              isExternal={!!target}
                              isBlank={!!target}
                            >
                              {logoImage && (
                                <div className={styles.image}>
                                  <GatsbyImage
                                    image={resolveAsImage(logoImage)}
                                    alt={title}
                                    className={styles.logoImage}
                                    loading="lazy"
                                  />
                                </div>
                              )}
                              <div className={styles.secondaryLinkContent}>
                                <div
                                  className={
                                    styles.secondaryLinkTitleContainer
                                  }
                                >
                                  <span className={styles.secondaryLinkTitle}>
                                    {title}
                                  </span>
                                  {newBadge && (
                                    <div className={styles.newBadge}>
                                      <span>NEW</span>
                                    </div>
                                  )}
                                </div>
                                <span
                                  className={styles.secondaryLinkDescription}
                                >
                                  {description}
                                </span>
                              </div>
                            </Permalink>
                          )
                        },
                        filteredLinks
                      )}
                    </div>
                    {banner && (
                      <Permalink to={banner.url} className={styles.banner}>
                        {banner.icon && (
                          <div className={styles.image}>
                            <img
                              src={resolveAsAttachment(banner.icon)}
                              alt={banner.title}
                            />
                          </div>
                        )}
                        <div className={styles.content}>
                          <FreeText type="m" className={styles.bannerTitle}>
                            {banner.title}
                          </FreeText>
                          {banner.description && (
                            <FreeText
                              type="sm"
                              className={styles.bannerDescription}
                            >
                              {banner.description}
                            </FreeText>
                          )}
                          <Icon
                            icon={getIconByName('arrow-right')}
                            fill="#757575"
                            size={20}
                          />
                        </div>
                      </Permalink>
                    )}
                  </>
                )
              )}
            </HeaderMenuPrimaryLink>
          )
        }, links)}
      </div>
    </nav>
  )
}
